// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullAdminRegatta,
  $FullRegattaResultTable,
  $FullRegattaResultTableRace,
} from 'common.data';

// Global Components
import * as React from 'react';
import {
  Card,
  Col,
  Input,
  Row,
} from 'reactstrap';
import {
  Loader,
} from '@vroskus/library-components';

// Helpers
import _ from 'lodash';
import {
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  defaultGetData,
} from '../../../../helpers';

// Components
import ResultTile from '../../../common/ResultTile';
import RegattaResultTableRaceSelectorSection from '../RegattaResultTableRaceSelectorSection';

// Services
import Services from '../../../../services';

// Types
import type {
  $ActiveRegattaResourceTableRaces,
  $TileResult,
} from '../../../../types';

type $Props = $Translate & {
  id: string;
  item: $FullAdminRegatta;
};

type $State = {
  activeRegattaResourceTableRaces: $ActiveRegattaResourceTableRaces;
  activeResults: Array<$TileResult>;
  data: Array<$FullRegattaResultTable>;
  dissapear: boolean;
  loading: boolean;
  mode: 'position' | 'roundings' | 'time';
};

class RegattaResultTilesSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      activeRegattaResourceTableRaces: {
      },
      activeResults: [],
      data: [],
      dissapear: false,
      loading: false,
      mode: 'position',
    };
  }

  componentDidMount(): void {
    this.initialGetData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      item,
    } = this.props;

    if (isDiff(
      item.id,
      prevProps.item.id,
    )) {
      this.initialGetData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          item,
        } = this.props;

        const models = ['RegattaResultTable', 'RegattaResultTableEntry', 'RegattaResultTableRace'];

        if (item.id === data.RegattaId && _.includes(
          models,
          data.modelName,
        )) {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      item,
    } = this.props;

    const RegattaId = item.id;

    await defaultGetData({
      action: async () => {
        if (RegattaId !== null) {
          const data = await Services.Api.getManyRegattaResultTable({
            RegattaId,
          });

          await this.setStateAsync({
            data,
            loading: false,
          });

          await this.getActiveResults();
        }
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  async getActiveResults(): Promise<void> {
    const {
      activeRegattaResourceTableRaces,
      data,
    } = this.state;

    let results: Array<$TileResult> = [];

    _.forEach(
      activeRegattaResourceTableRaces,
      (regattaResultTableRaceId: string, regattaResultTableId: string) => {
        const regattaResultTable: $FullRegattaResultTable | undefined = _.find(
          data,
          {
            id: regattaResultTableId,
          },
        );

        if (regattaResultTable) {
          const regattaResultTableRace: $FullRegattaResultTableRace | undefined = _.find(
            regattaResultTable.RegattaResultTableRaces,
            {
              id: regattaResultTableRaceId,
            },
          );

          if (regattaResultTableRace) {
            const raceResults: Array<$TileResult> = regattaResultTableRace
              .RegattaResultTableRaceResults.map((regattaResultTableRaceResult) => {
                const regattaResultTableEntry = _.find(
                  regattaResultTable.RegattaResultTableEntries,
                  {
                    id: regattaResultTableRaceResult.RegattaResultTableEntryId,
                  },
                );

                if (!regattaResultTableEntry) {
                  throw new Error('RegattaResultTableRaceResult RegattaResultTableEntry not found');
                }

                return {
                  ...regattaResultTableRaceResult,
                  RegattaResultTable: regattaResultTable,
                  RegattaResultTableEntry: regattaResultTableEntry,
                  RegattaResultTableRace: regattaResultTableRace,
                };
              });

            results = [...results, ...raceResults];
          }
        }
      },
    );

    const sortedResults: Array<$TileResult> = _.orderBy(
      results,
      [
        'RegattaResultTableEntry.RegattaEntry.Name',
        'RegattaResultTableEntry.RegattaEntry.RegistrationNo',
      ],
      ['asc', 'asc'],
    );

    await this.setStateAsync({
      activeResults: sortedResults,
    });
  }

  async initialGetData(): Promise<void> {
    await this.setStateAsync({
      activeRegattaResourceTableRaces: {
      },
      activeResults: [],
      loading: true,
    });

    await this.getData();
  }

  renderSettingsSection() {
    const {
      t,
    } = this.props;
    const {
      dissapear,
      mode,
    } = this.state;

    return (
      <Row>
        <Col
          sm={8}
          xs={6}
        >
          <span className={'ml-1'}>{t('Save result')}</span>
          <label
            className={'checkbox c-checkbox c-checkbox-rounded m-1 ml-3'}
            htmlFor={'positionSwitch'}
          >
            <Input
              checked={mode === 'position'}
              id={'positionSwitch'}
              onClick={() => {
                this.setState({
                  mode: 'position',
                });
              }}
              type={'checkbox'}
            />
            <span className={'fa fa-check'} />
            {t('position')}
          </label>
          <label
            className={'checkbox c-checkbox c-checkbox-rounded m-1 ml-3'}
            htmlFor={'timeSwitch'}
          >
            <Input
              checked={mode === 'time'}
              id={'timeSwitch'}
              onClick={() => {
                this.setState({
                  mode: 'time',
                });
              }}
              type={'checkbox'}
            />
            <span className={'fa fa-check'} />
            {t('time')}
          </label>
          <label
            className={'checkbox c-checkbox c-checkbox-rounded m-1 ml-3'}
            htmlFor={'roundingsSwitch'}
          >
            <Input
              checked={mode === 'roundings'}
              id={'roundingsSwitch'}
              onClick={() => {
                this.setState({
                  mode: 'roundings',
                });
              }}
              type={'checkbox'}
            />
            <span className={'fa fa-check'} />
            {t('roundings')}
          </label>
        </Col>
        <Col className={'text-right'}>
          <label
            className={'checkbox c-checkbox m-1'}
            htmlFor={'dissapearSwitch'}
          >
            <Input
              checked={dissapear}
              id={'dissapearSwitch'}
              onClick={() => {
                this.setState({
                  dissapear: !dissapear,
                });
              }}
              type={'checkbox'}
            />
            <span className={'fa fa-check'} />
            {t('Hide finished')}
          </label>
        </Col>
      </Row>
    );
  }

  renderTilesSection() {
    const {
      item,
    } = this.props;
    const {
      activeResults,
      dissapear,
      mode,
    } = this.state;

    let resultsToShow: Array<$TileResult> = activeResults;

    if (dissapear === true) {
      resultsToShow = activeResults.filter((
        result: $TileResult,
      ) => !(result.Position !== null || result.ET !== 0));
    }

    return (
      <Row className={'no-gutters'}>
        {resultsToShow.map((result: $TileResult) => (
          <Col
            className={'p-1'}
            key={result.id}
            lg={2}
            xs={6}
          >
            <ResultTile
              mode={mode}
              regatta={item}
              result={result}
            />
          </Col>
        ))}
      </Row>
    );
  }

  renderContent() {
    const {
      t,
    } = this.props;
    const {
      activeResults,
    } = this.state;

    return activeResults.length !== 0 ? (
      <Card className={'p-1'}>
        {this.renderSettingsSection()}
        <hr className={'mt-0'} />
        {this.renderTilesSection()}
      </Card>
    ) : (
      <Card>
        <div className={'text-muted text-center p-3'}>
          {t('Add result table entries')}
        </div>
      </Card>
    );
  }

  render() {
    const {
      id,
    } = this.props;
    const {
      data,
      loading,
    } = this.state;

    return (
      <div id={id}>
        <Loader
          loading={loading === true}
          overlay
        >
          <RegattaResultTableRaceSelectorSection
            data={data}
            onChange={async (activeRegattaResourceTableRaces: $ActiveRegattaResourceTableRaces) => {
              await this.setStateAsync({
                activeRegattaResourceTableRaces,
              });

              await this.getActiveResults();
            }}
          />
          {this.renderContent()}
        </Loader>
      </div>
    );
  }
}

export default RegattaResultTilesSectionView;
